// Story 16.6 — la cuenta del usuario: sus datos, cambiar la contraseña y sus
// API keys. Cambiar la contraseña cierra todas las demás sesiones y rota la de
// este navegador (5-2 D-8); las API keys siguen activas y se revocan acá.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { KeyRound } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useMe } from '@/hooks/queries/use-me';
import { reconnectWs } from '@/hooks/use-ws-event';
import { ApiError, api } from '@/lib/api-client';
import { ROLE_LABELS } from '@/lib/organization-roles';
import { queryKeys } from '@/lib/query-client';

interface ApiKeyItem {
  id: number;
  label: string;
  preview: string;
  lastUsedAt: string | null;
  createdAt: string;
  revokedAt: string | null;
}

const PLATFORM_ROLE_LABELS = { admin: 'Admin', support: 'Soporte', member: 'Cliente' } as const;

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function PasswordCard(): React.ReactElement {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  const change = useMutation({
    mutationFn: () => api.post('/api/v1/auth/password', { current, next }),
  });

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (next.length < 8) return setError('La contraseña nueva necesita al menos 8 caracteres.');
    if (next !== confirm) return setError('Las contraseñas nuevas no coinciden.');
    setError(null);
    try {
      await change.mutateAsync();
      setCurrent('');
      setNext('');
      setConfirm('');
      // La sesión de este navegador cambió de token: el WebSocket se reabre con la nueva.
      reconnectWs();
      void qc.invalidateQueries({ queryKey: queryKeys.me() });
      toast.success('Contraseña cambiada. Se cerraron tus otras sesiones.');
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 403
          ? 'La contraseña actual no es correcta.'
          : errorMessage(err, 'No se pudo cambiar la contraseña.'),
      );
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Cambiar contraseña</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="max-w-sm space-y-3">
          <div className="space-y-1">
            <Label htmlFor="pwd-current">Contraseña actual</Label>
            <Input
              id="pwd-current"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              maxLength={72}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pwd-next">Contraseña nueva</Label>
            <Input
              id="pwd-next"
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
              maxLength={72}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="pwd-confirm">Repite la contraseña nueva</Label>
            <Input
              id="pwd-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              maxLength={72}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
          <Button type="submit" disabled={change.isPending}>
            {change.isPending ? 'Cambiando…' : 'Cambiar contraseña'}
          </Button>
          <p className="text-xs text-muted-foreground">
            Se cierran tus sesiones en otros navegadores. Tus API keys siguen activas: revisa abajo
            las que no reconozcas.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

function ApiKeysCard(): React.ReactElement {
  const qc = useQueryClient();
  const keys = useQuery({
    queryKey: ['auth', 'api-keys'],
    queryFn: () => api.get<ApiKeyItem[]>('/api/v1/auth/api-keys'),
  });
  const revoke = useMutation({
    mutationFn: (id: number) => api.delete(`/api/v1/auth/api-keys/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'api-keys'] }),
  });
  const active = (keys.data ?? []).filter((k) => !k.revokedAt);

  async function handleRevoke(key: ApiKeyItem): Promise<void> {
    if (!window.confirm(`¿Revocar la API key "${key.label}"? Lo que la use deja de funcionar.`))
      return;
    try {
      await revoke.mutateAsync(key.id);
      toast.success(`API key "${key.label}" revocada`);
    } catch (err) {
      toast.error(errorMessage(err, 'No se pudo revocar la API key'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">API keys</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Las crea <code className="font-mono">revel-bots auth login</code> para la CLI.
        </p>
        {keys.isLoading ? (
          <p className="text-sm text-muted-foreground">Cargando…</p>
        ) : active.length === 0 ? (
          <p className="text-sm text-muted-foreground">No tienes API keys activas.</p>
        ) : (
          <ul className="divide-y divide-border">
            {active.map((k) => (
              <li key={k.id} className="flex items-center gap-3 py-2 text-sm">
                <KeyRound className="h-4 w-4 text-muted-foreground" aria-hidden />
                <span className="flex-1">
                  <span className="font-medium">{k.label}</span>
                  <span className="ml-2 font-mono text-xs text-muted-foreground">{k.preview}…</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {k.lastUsedAt
                    ? `Último uso: ${new Date(k.lastUsedAt).toLocaleDateString()}`
                    : 'Sin usar'}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={revoke.isPending}
                  onClick={() => void handleRevoke(k)}
                >
                  Revocar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

export function AccountPage(): React.ReactElement {
  const { data: me } = useMe();

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Tu cuenta</h1>
        <p className="text-sm text-muted-foreground">
          {me?.name ? `${me.name} · ` : ''}
          {me?.email}
          {me && <> · {PLATFORM_ROLE_LABELS[me.role]}</>}
        </p>
      </header>

      {me && me.organizations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tus organizaciones</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {me.organizations.map((o) => (
                <li key={o.id}>
                  <Link
                    to="/organizations/$organizationId"
                    params={{ organizationId: String(o.id) }}
                    className="hover:underline"
                  >
                    {o.name}
                  </Link>
                  <span className="ml-2 text-muted-foreground">{ROLE_LABELS[o.role]}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <PasswordCard />
      <ApiKeysCard />
    </div>
  );
}
